import React from 'react';
import { Clock3, Users, Edit2, MoreVertical, Ban } from 'lucide-react';
import './ServiceTileHead.css';

const AVATAR_PALETTE = [
  { bg: 'rgba(243, 119, 77, 0.18)', fg: '#F3774D' },
  { bg: 'rgba(167, 139, 250, 0.18)', fg: '#A78BFA' },
  { bg: 'rgba(248, 113, 113, 0.18)', fg: '#F87171' },
  { bg: 'rgba(96, 165, 250, 0.18)', fg: '#60A5FA' },
  { bg: 'rgba(251, 191, 36, 0.18)', fg: '#FBBF24' },
  { bg: 'rgba(244, 114, 182, 0.18)', fg: '#F472B6' },
];

export const pickAvatarPalette = (key) => {
  const source = String(key || '');
  let hash = 0;
  for (let i = 0; i < source.length; i += 1) {
    hash = (hash * 31 + source.charCodeAt(i)) >>> 0;
  }
  return AVATAR_PALETTE[hash % AVATAR_PALETTE.length];
};

const formatPrice = (price) => {
  if (price === null || price === undefined || price === '') return null;
  const num = Number(price);
  if (Number.isNaN(num)) return String(price);
  return `₹${num}`;
};

/**
 * F6 — Per-service tile header row.
 *
 * Avatar (deterministic palette) + name + ACTIVE chip,
 * meta line (duration / capacity / price / mode chip),
 * right cluster: toggle + edit pencil + kebab.
 */
const ServiceTileHead = ({
  service,
  isActive,
  durationMinutes,
  capacityPerSlot,
  price,
  onToggleActive,
  onEdit,
  onMenu,
  holdBadge = null,
  disabled = false,
}) => {
  const name = service?.serviceName || 'Untitled service';
  const initial = (name.charAt(0) || 'S').toUpperCase();
  const palette = pickAvatarPalette(service?.serviceUuid || name);
  const mode = String(service?.appointmentMode || 'IN_STORE').toUpperCase();
  const priceLabel = formatPrice(price ?? service?.depositAmount);

  return (
    <div className="appointments-v3-tile-head">
      <div className="appointments-v3-tile-head-main">
        <div
          className="appointments-v3-tile-avatar"
          style={{ background: palette.bg, color: palette.fg }}
          aria-hidden="true"
        >
          {initial}
        </div>
        <div className="appointments-v3-tile-head-text">
          <div className="appointments-v3-tile-head-title-line">
            <span className="appointments-v3-tile-head-name">{name}</span>
            <span
              className={`appointments-v3-tile-head-chip ${isActive ? 'is-active' : 'is-inactive'}`}
            >
              {isActive ? 'ACTIVE' : 'INACTIVE'}
            </span>
          </div>
          {holdBadge && (
            <div className="appointments-v3-tile-head-hold" role="status">
              <Ban size={12} aria-hidden="true" />
              <span>
                {`Unavailable ${holdBadge.fromTime}–${holdBadge.toTime} today`}
                {holdBadge.extraCount > 0 ? ` +${holdBadge.extraCount} more` : ''}
              </span>
            </div>
          )}
          <div className="appointments-v3-tile-head-meta">
            {durationMinutes != null && (
              <span className="appointments-v3-tile-head-meta-item">
                <Clock3 size={12} aria-hidden="true" />
                {durationMinutes} min
              </span>
            )}
            {capacityPerSlot != null && (
              <span className="appointments-v3-tile-head-meta-item">
                <Users size={12} aria-hidden="true" />
                {capacityPerSlot}/slot
              </span>
            )}
            {priceLabel && (
              <span className="appointments-v3-tile-head-meta-item">{priceLabel}</span>
            )}
            <span className="appointments-v3-tile-head-mode">{mode.replace('_', '-')}</span>
          </div>
        </div>
      </div>

      <div className="appointments-v3-tile-head-actions">
        {onToggleActive && (
          <button
            type="button"
            role="switch"
            aria-checked={isActive}
            aria-label={`${isActive ? 'Deactivate' : 'Activate'} ${name}`}
            disabled={disabled}
            onClick={() => onToggleActive(!isActive)}
            className={`appointments-v3-tile-switch ${isActive ? 'is-on' : ''}`}
          >
            <span className="appointments-v3-tile-switch-thumb" aria-hidden="true" />
          </button>
        )}
        {onEdit && (
          <button
            type="button"
            className="appointments-v3-tile-icon-btn"
            aria-label={`Edit ${name}`}
            disabled={disabled}
            onClick={onEdit}
          >
            <Edit2 size={14} aria-hidden="true" />
          </button>
        )}
        {onMenu && (
          <button
            type="button"
            className="appointments-v3-tile-icon-btn"
            aria-label={`${name} options`}
            disabled={disabled}
            onClick={onMenu}
          >
            <MoreVertical size={14} aria-hidden="true" />
          </button>
        )}
      </div>
    </div>
  );
};

export default ServiceTileHead;
